import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  GetActiveExpenseCategories,
  GetExpenseCategories,
  CreateExpenseCategory,
  UpdateExpenseCategory,
  DeleteExpenseCategory,
  GetActiveExpenseSubcategories,
  GetExpenseSubcategories,
  CreateExpenseSubcategory,
  UpdateExpenseSubcategory,
  DeleteExpenseSubcategory,
  CreateExpense,
  GetExpenses,
} from "../Api";
import {
  DEFAULT_STALE_TIME,
  LOOKUP_STALE_TIME,
  ServiceQueryOptions,
  unwrapApiResponse,
} from "./query_helpers";

export const expenseQueryKeys = {
  activeExpenseCategories: () => ["activeExpenseCategories"] as const,
  expenseCategories: (params?: Parameters<typeof GetExpenseCategories>[0]) =>
    ["expenseCategories", params] as const,
  activeExpenseSubcategories: () => ["activeExpenseSubcategories"] as const,
  expenseSubcategories: (
    params?: Parameters<typeof GetExpenseSubcategories>[0],
  ) => ["expenseSubcategories", params] as const,
  expenses: (params?: Parameters<typeof GetExpenses>[0]) =>
    ["expenses", params] as const,
};

export const invalidateExpenseQueries = (queryClient: any) => {
  queryClient.invalidateQueries({ queryKey: ["activeExpenseCategories"] });
  queryClient.invalidateQueries({ queryKey: ["expenseCategories"] });
  queryClient.invalidateQueries({ queryKey: ["activeExpenseSubcategories"] });
  queryClient.invalidateQueries({ queryKey: ["expenseSubcategories"] });
  queryClient.invalidateQueries({ queryKey: ["expenses"] });
};

export const useActiveExpenseCategoriesQuery = (
  options?: ServiceQueryOptions,
) => {
  return useQuery({
    staleTime: LOOKUP_STALE_TIME,
    ...options,
    queryKey: expenseQueryKeys.activeExpenseCategories(),
    queryFn: async () => unwrapApiResponse(await GetActiveExpenseCategories()),
  });
};

export const useActiveExpenseCategoriesRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () =>
      queryClient.fetchQuery({
        staleTime: LOOKUP_STALE_TIME,
        queryKey: expenseQueryKeys.activeExpenseCategories(),
        queryFn: async () =>
          unwrapApiResponse(await GetActiveExpenseCategories()),
      }),
  });
};

export const useExpenseCategoriesQuery = (
  params: Parameters<typeof GetExpenseCategories>[0],
  options?: ServiceQueryOptions,
) => {
  return useQuery({
    staleTime: DEFAULT_STALE_TIME,
    ...options,
    queryKey: expenseQueryKeys.expenseCategories(params),
    queryFn: async () => unwrapApiResponse(await GetExpenseCategories(params)),
  });
};

export const useExpenseCategoriesRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: Parameters<typeof GetExpenseCategories>[0]) =>
      queryClient.fetchQuery({
        staleTime: DEFAULT_STALE_TIME,
        queryKey: expenseQueryKeys.expenseCategories(params),
        queryFn: async () =>
          unwrapApiResponse(await GetExpenseCategories(params)),
      }),
  });
};

export const useActiveExpenseSubcategoriesQuery = (
  options?: ServiceQueryOptions,
) => {
  return useQuery({
    staleTime: LOOKUP_STALE_TIME,
    ...options,
    queryKey: expenseQueryKeys.activeExpenseSubcategories(),
    queryFn: async () =>
      unwrapApiResponse(await GetActiveExpenseSubcategories()),
  });
};

export const useActiveExpenseSubcategoriesRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () =>
      queryClient.fetchQuery({
        staleTime: LOOKUP_STALE_TIME,
        queryKey: expenseQueryKeys.activeExpenseSubcategories(),
        queryFn: async () =>
          unwrapApiResponse(await GetActiveExpenseSubcategories()),
      }),
  });
};

export const useExpenseSubcategoriesQuery = (
  params: Parameters<typeof GetExpenseSubcategories>[0],
  options?: ServiceQueryOptions,
) => {
  return useQuery({
    staleTime: DEFAULT_STALE_TIME,
    ...options,
    queryKey: expenseQueryKeys.expenseSubcategories(params),
    queryFn: async () =>
      unwrapApiResponse(await GetExpenseSubcategories(params)),
  });
};

export const useExpenseSubcategoriesRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: Parameters<typeof GetExpenseSubcategories>[0]) =>
      queryClient.fetchQuery({
        staleTime: DEFAULT_STALE_TIME,
        queryKey: expenseQueryKeys.expenseSubcategories(params),
        queryFn: async () =>
          unwrapApiResponse(await GetExpenseSubcategories(params)),
      }),
  });
};

export const useExpensesQuery = (
  params: Parameters<typeof GetExpenses>[0],
  options?: ServiceQueryOptions,
) => {
  return useQuery({
    staleTime: DEFAULT_STALE_TIME,
    ...options,
    queryKey: expenseQueryKeys.expenses(params),
    queryFn: async () => {
      const res = await GetExpenses(params);
      if (!res?.success) {
        return { data: [], total: 0 };
      }
      return {
        data: res.data || [],
        total: res.meta?.total || 0,
      };
    },
  });
};

export const useExpensesRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: Parameters<typeof GetExpenses>[0]) =>
      queryClient.fetchQuery({
        staleTime: DEFAULT_STALE_TIME,
        queryKey: expenseQueryKeys.expenses(params),
        queryFn: async () => {
          const res = await GetExpenses(params);
          if (!res?.success) {
            return { data: [], total: 0 };
          }
          return {
            data: res.data || [],
            total: res.meta?.total || 0,
          };
        },
      }),
  });
};

export const useCreateExpenseCategoryMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: Parameters<typeof CreateExpenseCategory>[0]) =>
      CreateExpenseCategory(payload),
    onSuccess: (res) => {
      if (res?.success) {
        invalidateExpenseQueries(queryClient);
      }
    },
  });
};

export const useUpdateExpenseCategoryMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: Parameters<typeof UpdateExpenseCategory>[0]) =>
      UpdateExpenseCategory(payload),
    onSuccess: (res) => {
      if (res?.success) {
        invalidateExpenseQueries(queryClient);
      }
    },
  });
};

export const useDeleteExpenseCategoryMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: Parameters<typeof DeleteExpenseCategory>[0]) =>
      DeleteExpenseCategory(payload),
    onSuccess: (res) => {
      if (res?.success) {
        invalidateExpenseQueries(queryClient);
      }
    },
  });
};

export const useCreateExpenseSubcategoryMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: Parameters<typeof CreateExpenseSubcategory>[0]) =>
      CreateExpenseSubcategory(payload),
    onSuccess: (res) => {
      if (res?.success) {
        invalidateExpenseQueries(queryClient);
      }
    },
  });
};

export const useUpdateExpenseSubcategoryMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: Parameters<typeof UpdateExpenseSubcategory>[0]) =>
      UpdateExpenseSubcategory(payload),
    onSuccess: (res) => {
      if (res?.success) {
        invalidateExpenseQueries(queryClient);
      }
    },
  });
};

export const useDeleteExpenseSubcategoryMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: Parameters<typeof DeleteExpenseSubcategory>[0]) =>
      DeleteExpenseSubcategory(payload),
    onSuccess: (res) => {
      if (res?.success) {
        invalidateExpenseQueries(queryClient);
      }
    },
  });
};

export const useCreateExpenseMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: Parameters<typeof CreateExpense>[0]) =>
      CreateExpense(payload),
    onSuccess: (res) => {
      if (res?.success) {
        invalidateExpenseQueries(queryClient);
      }
    },
  });
};

export const useExpenseService = (config?: {
  fetchActiveExpenseCategoriesEnabled?: boolean;
  fetchExpenseCategoriesParams?: Parameters<typeof GetExpenseCategories>[0];
  fetchExpenseCategoriesEnabled?: boolean;
  fetchActiveExpenseSubcategoriesEnabled?: boolean;
  fetchExpenseSubcategoriesParams?: Parameters<
    typeof GetExpenseSubcategories
  >[0];
  fetchExpenseSubcategoriesEnabled?: boolean;
  fetchExpensesParams?: Parameters<typeof GetExpenses>[0];
  fetchExpensesEnabled?: boolean;
}) => {
  const activeExpenseCategoriesQuery = useActiveExpenseCategoriesQuery({
    enabled: config?.fetchActiveExpenseCategoriesEnabled !== false,
  });

  const expenseCategoriesQuery = useExpenseCategoriesQuery(
    config?.fetchExpenseCategoriesParams,
    {
      enabled:
        config?.fetchExpenseCategoriesEnabled !== false &&
        config?.fetchExpenseCategoriesParams !== undefined &&
        config?.fetchExpenseCategoriesParams !== null,
    },
  );

  const activeExpenseSubcategoriesQuery = useActiveExpenseSubcategoriesQuery({
    enabled: config?.fetchActiveExpenseSubcategoriesEnabled !== false,
  });

  const expenseSubcategoriesQuery = useExpenseSubcategoriesQuery(
    config?.fetchExpenseSubcategoriesParams,
    {
      enabled:
        config?.fetchExpenseSubcategoriesEnabled !== false &&
        config?.fetchExpenseSubcategoriesParams !== undefined &&
        config?.fetchExpenseSubcategoriesParams !== null,
    },
  );

  const expensesQuery = useExpensesQuery(config?.fetchExpensesParams, {
    enabled:
      config?.fetchExpensesEnabled !== false &&
      config?.fetchExpensesParams !== undefined &&
      config?.fetchExpensesParams !== null,
  });

  const createExpenseCategoryMutation = useCreateExpenseCategoryMutation();
  const updateExpenseCategoryMutation = useUpdateExpenseCategoryMutation();
  const deleteExpenseCategoryMutation = useDeleteExpenseCategoryMutation();
  const createExpenseSubcategoryMutation =
    useCreateExpenseSubcategoryMutation();
  const updateExpenseSubcategoryMutation =
    useUpdateExpenseSubcategoryMutation();
  const deleteExpenseSubcategoryMutation =
    useDeleteExpenseSubcategoryMutation();
  const createExpenseMutation = useCreateExpenseMutation();

  return {
    activeExpenseCategoriesQuery,
    expenseCategoriesQuery,
    activeExpenseSubcategoriesQuery,
    expenseSubcategoriesQuery,
    expensesQuery,
    createExpenseCategoryMutation,
    updateExpenseCategoryMutation,
    deleteExpenseCategoryMutation,
    createExpenseSubcategoryMutation,
    updateExpenseSubcategoryMutation,
    deleteExpenseSubcategoryMutation,
    createExpenseMutation,
  };
};
